import React, { useState, memo, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Typography,
  makeStyles,
  IconButton,
  Paper,
  Box,
  Dialog,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from "@material-ui/core";
// import { Alert } from "@material-ui/lab";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import StyledDialog from "../../../../../common/models/Dialog";

// Import section components
import CMODetailsSection from "./sections/CMODetailsSection";
import AdminDetailsSection from "./sections/AdminDetailsSection";
// import SiteDetailsSection from "./sections/SiteDetailsSection";
// import PlantDetailsSection from "./sections/PlantDetailsSection";
import PlantConfigurationSection from "./sections/PlantConfigurationSection";
import CmoLevelConfigSection from "./sections/CmoLevelConfigSection";
import ShipmentConfigSection from "./sections/ShipmentConfigSection";
// import BatchAllocationPdfFieldsSection from "./sections/BatchAllocationPdfFieldsSection";
import POConfigurationsSection from "./sections/POConfigurationsSection";
import DocumentTypesSection from "./sections/DocumentTypesSection";
import DocumentFetchDetailsSection from "./sections/DocumentFetchDetailsSection";
import PurchasingDetailsSection from "./sections/PurchasingDetailsSection";
import DeliveryAddressSection from "./sections/DeliveryAddressSection";
import PickupAddressSection from "./sections/PickupAddressSection";
import MainAddressSection from "./sections/MainAddressSection";
import ManufacturingPlantDetailsSection from "./sections/ManufacturingPlantDetailsSection";
import { appUrl } from "../../../../../../utils/config";
import axios from "axios";
import { useHistory } from "react-router-dom";
import "../CMOConfigDetails.css";
import { useApiBaseUrl } from "../../CommonMethods/CommonMethods";
import ProductsTypesSection from "./sections/ProductsTypesSection";
import SiteIDPlantIDDetails from "./sections/SiteIDPlantIDDetails";
import IngestionConfigurationSection from "./sections/IngestionConfigurationSection";

// Memoize section components to prevent unnecessary re-renders
const MemoizedCMODetailsSection = memo(
  CMODetailsSection,
  (prevProps, nextProps) => {
    // Only re-render if key props change
    const keyPropsEqual =
      prevProps.editMode === nextProps.editMode &&
      prevProps.initialData === nextProps.initialData;

    if (!keyPropsEqual) {
    }

    return keyPropsEqual;
  }
);
const MemoizedAdminDetailsSection = memo(AdminDetailsSection);
// const MemoizedSiteDetailsSection = memo(SiteDetailsSection); 
const MemoizedSiteDetailsSection = memo(SiteIDPlantIDDetails);
// const MemoizedPlantDetailsSection = memo(PlantDetailsSection);

// Custom comparison for PlantConfigurationSection to prevent unnecessary re-renders
const MemoizedPlantConfigurationSection = memo(
  PlantConfigurationSection,
  (prevProps, nextProps) => {
    // Re-render only if these key props change
    return (
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.vendorMosymphonyID === nextProps.vendorMosymphonyID &&
      prevProps.brandOwnerMosymphonyID === nextProps.brandOwnerMosymphonyID &&
      JSON.stringify(prevProps.formData) === JSON.stringify(nextProps.formData)
    );
  }
);

const MemoizedCmoLevelConfigSection = memo(CmoLevelConfigSection);
const MemoizedShipmentConfigSection = memo(ShipmentConfigSection);
// const MemoizedBatchAllocationPdfFieldsSection = memo(BatchAllocationPdfFieldsSection);
const MemoizedPOConfigurationsSection = memo(POConfigurationsSection);
const MemoizedDocumentTypesSection = memo(DocumentTypesSection);
const MemoizedProductTypesSection = memo(ProductsTypesSection);
const MemoizedDocumentFetchDetailsSection = memo(DocumentFetchDetailsSection);
const MemoizedPurchasingDetailsSection = memo(PurchasingDetailsSection);
const MemoizedIngestionConfigurationSection = memo(IngestionConfigurationSection);

// Custom comparison for address sections to prevent unnecessary re-renders
const MemoizedDeliveryAddressSection = memo(
  DeliveryAddressSection,
  (prevProps, nextProps) => {
    return (
      prevProps.selectedPlantTab === nextProps.selectedPlantTab &&
      prevProps.editMode === nextProps.editMode &&
      prevProps.disableEdit === nextProps.disableEdit &&
      prevProps.vendorMosymphonyID === nextProps.vendorMosymphonyID &&
      prevProps.brandOwnerMosymphonyID === nextProps.brandOwnerMosymphonyID &&
      prevProps.plantInfo?.manufacturingPlantID === nextProps.plantInfo?.manufacturingPlantID
    );
  }
);

const MemoizedPickupAddressSection = memo(
  PickupAddressSection,
  (prevProps, nextProps) => {
    return (
      prevProps.selectedPlantTab === nextProps.selectedPlantTab &&
      prevProps.editMode === nextProps.editMode &&
      prevProps.disableEdit === nextProps.disableEdit &&
      prevProps.vendorMosymphonyID === nextProps.vendorMosymphonyID &&
      prevProps.brandOwnerMosymphonyID === nextProps.brandOwnerMosymphonyID &&
      prevProps.plantInfo?.manufacturingPlantID === nextProps.plantInfo?.manufacturingPlantID
    );
  }
);

const MemoizedMainAddressSection = memo(
  MainAddressSection,
  (prevProps, nextProps) => {
    return (
      prevProps.selectedPlantTab === nextProps.selectedPlantTab &&
      prevProps.editMode === nextProps.editMode &&
      prevProps.disableEdit === nextProps.disableEdit &&
      prevProps.vendorMosymphonyID === nextProps.vendorMosymphonyID &&
      prevProps.brandOwnerMosymphonyID === nextProps.brandOwnerMosymphonyID &&
      prevProps.plantInfo?.manufacturingPlantID === nextProps.plantInfo?.manufacturingPlantID
    );
  }
);

// Custom comparison for ManufacturingPlantDetailsSection to prevent unnecessary re-renders
const MemoizedManufacturingPlantDetailsSection = memo(
  ManufacturingPlantDetailsSection,
  (prevProps, nextProps) => {
    // Only re-render if these key props change
    return (
      prevProps.initialData?.manufacturingPlantID === nextProps.initialData?.manufacturingPlantID &&
      prevProps.initialData?.siteID === nextProps.initialData?.siteID &&
      prevProps.initialData?.siteShortName === nextProps.initialData?.siteShortName &&
      prevProps.initialData?.manufacturingPlantShortName === nextProps.initialData?.manufacturingPlantShortName &&
      prevProps.editMode === nextProps.editMode &&
      prevProps.disableEdit === nextProps.disableEdit &&
      prevProps.vendorMosymphonyID === nextProps.vendorMosymphonyID &&
      prevProps.brandOwnerMosymphonyID === nextProps.brandOwnerMosymphonyID
    );
  }
);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    width: "100%",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: 32,
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  iconButtonStyle: {
    color: "black",
    border: "1px solid black",
    borderRadius: "5px",
    padding: "4px",
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  updateButton: {
    marginTop: 32,
    minWidth: 200,
    background: "#ec6535",
    color: "#fff",
    border: 0,
    borderRadius: 6,
    padding: "12px 32px",
    fontWeight: 600,
    fontSize: 16,
    cursor: "pointer",
  },
  disabledButton: {
    marginTop: 32,
    minWidth: 200,
    background: "#ccc",
    color: "#fff",
    border: 0,
    borderRadius: 6,
    padding: "12px 32px",
    fontWeight: 600,
    fontSize: 16,
    cursor: "not-allowed",
    opacity: 0.6,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    maxWidth: "1270px",
    margin: "0 auto",
  },
  sectionActions: {
    display: "flex",
    gap: theme.spacing(2),
  },
  sectionButton: {
    minWidth: 150,
    fontWeight: 600,
    fontSize: 14,
    borderRadius: 6,
    padding: "8px 24px",
  },
  saveButton: {
    borderRadius: "5rem",
    marginLeft: "1rem",
    textTransform: "none !important",
  },
  discardButton: {
    borderRadius: "5rem",
    textTransform: "none !important",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 0,
    marginBottom: 0,
  },
  updateChangesButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 40,
    // padding: "0 30px",
    // height: 44,
    // fontWeight: 600,
    // fontSize: 20,
    // minWidth: 200,
    boxShadow: "none",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "60px 0 20px",
    "&:hover": {
      backgroundColor: "#d45328",
      boxShadow: "none",
    },
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
}));



export default function UpdateCmoConfiguration() {
  const classes = useStyles();
  const location = useLocation();
  const { cmoOrgDetails, cmoVendorDetails, selectedCMOFormData } = location.state || {};

  // Process the incoming data
  const org = cmoOrgDetails?.CmoDetails?.[0] || cmoOrgDetails || {};
  const vendor = cmoVendorDetails?.vendorPlantDetails || cmoVendorDetails || {};

  const cmoSites = selectedCMOFormData?.vendorPlantDetails?.cmoSites || [];


  const API_BASE_URL = useApiBaseUrl(appUrl);

  // State to track site details changes for rebuilding plant tabs
  const [siteDetailsVersion, setSiteDetailsVersion] = useState(0);
  const siteDetailsRef = useRef(null);

  // State to track plant-specific section resets (for forcing remount on discard)
  const [plantSectionResetVersion, setPlantSectionResetVersion] = useState(0);

  // Get current cmoSites data, considering any edits from siteDetailsRef
  const currentCmoSites = useMemo(() => {
    // Try to get data from siteDetailsRef (if user is editing or has saved)
    if (siteDetailsRef.current && typeof siteDetailsRef.current.getFormData === 'function') {
      try {
        const currentData = siteDetailsRef.current.getFormData();
        if (currentData?.cmoSites) {
          return currentData.cmoSites;
        }
      } catch (error) {
        console.error('Failed to get current site data from ref:', error);
      }
    }
    // Fallback to initial data
    return cmoSites;
  }, [cmoSites, siteDetailsVersion]);

  // Check if there are any valid sites (with actual site IDs)
  const hasValidSites = useMemo(() => {
    return currentCmoSites.some(site => {
      const siteDetails = site.siteDetails || {};
      // A valid site has a siteID and is not marked for deletion
      return siteDetails.siteID && siteDetails.siteID.trim() !== '' && siteDetails.markForDelete !== true;
    });
  }, [currentCmoSites]);

  // Flatten all manufacturing plants from all sites for tabs
  const allManufacturingPlants = useMemo(() => {
    const plants = [];

    currentCmoSites.forEach(site => {
      const siteDetails = site.siteDetails || {};
      // Skip sites marked for deletion
      if (siteDetails.markForDelete === true) return;

      if (site.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList)) {
        site.manufacturingPlantDataList.forEach(plant => {
          const plantDetails = plant.mfgPlantDetails || plant;
          // Skip plants marked for deletion
          if (plant.markForDelete === true || plantDetails.markForDelete === true) return;

          if (plantDetails.mfgPlantShortName) {
            plants.push({
              siteID: siteDetails.siteID,
              siteShortName: siteDetails.siteShortName,
              manufacturingPlantID: plantDetails.mfgPlantID,
              manufacturingPlantShortName: plantDetails.mfgPlantShortName,
              collabrixCMOSiteID: plantDetails.collabrixCMOSiteID || siteDetails.collabrixCMOSiteID,
              collabrixMfgPlantID: plantDetails.collabrixMfgPlantID,
              aslNumber: plantDetails.aslNumber,
              lineSuiteDetails: plant.lineDetailsList && plant.lineDetailsList.length > 0
                ? plant.lineDetailsList.map(line => ({
                  lineSuiteID: line.lineID || "",
                  lineSuiteShortName: line.lineShortName || ""
                }))
                : [{ lineSuiteID: "", lineSuiteShortName: "" }],
              mainAddress: plant.mainAddress,
              pickupAddress: plant.pickupAddress,
              deliveryAddress: plant.deliveryAddress,
              plantTypeIDList: plant.plantTypeIDList,
              drugTypeIDList: plant.drugTypeIDList,
              supplyTypeIDList: plant.supplyTypeIDList,
              facilityTypeIDList: plant.facilityTypeIDList,
              // Freight forwarder — map from API response (key is `freightForwardersDetails`)
              // so the edit screen's plant section can display/edit it.
              freightForwarder: (() => {
                const ffd = plant?.freightForwardersDetails || plant?.freightForwarderDetails || {};
                return {
                  id: ffd.freightForwarderID || "",
                  name: ffd.freightForwarderName || "",
                  portal: ffd.portal || "",
                  billingCenter: ffd.billingCentreForShipment || ffd.billingCenterForShipment || "",
                  collabrixFreightForwarderID:
                    ffd.collabribrixFreightForwarderID || ffd.collabrixFreightForwarderID || null,
                };
              })()
            });
          }
        });
      }
    });
    return plants;
  }, [currentCmoSites, siteDetailsVersion]);

  // State for managing the selected tab in Manufacturing Plant Details
  const [selectedPlantTab, setSelectedPlantTab] = useState(0);

  // Reset tab index if it exceeds the number of plants (after deletion)
  useEffect(() => {
    if (selectedPlantTab >= allManufacturingPlants.length && allManufacturingPlants.length > 0) {
      setSelectedPlantTab(allManufacturingPlants.length - 1);
    } else if (allManufacturingPlants.length === 0) {
      setSelectedPlantTab(0);
    }
  }, [allManufacturingPlants.length, selectedPlantTab]);



  const history = useHistory();

  // State for success popup and redirection
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // New loading state for the update process
  const [dataElements, setDataElements] = useState([]);
  const [isDataElementsLoading, setIsDataElementsLoading] = useState(false);
  const [isIngestionConfigValid, setIsIngestionConfigValid] = useState(true);

  // Map API data to section initial values
  const initialCmoOrgDetails = {
    cmoFullName: org.cmoName || "",
    cmoShortName: org.cmoShortFormName || "",
    streetAddress1: org.streetAddress1 || "",
    streetAddress2: org.streetAddress2 || "",
    city: org.city || "",
    state: org.state || "",
    postCode: org.postCode || "",
    country: org.country || "",
    organizationCode: org.organizationCode || "",
    organizationId: org.organizationId || "",
    status: vendor.status || "Active",
  };

  const initialAdminDetails = {
    fullName: org.fullName || "",
    emailAddress: org.emailAddress || "",
    phoneNumber: org.phoneNumber || "",
  };

  const initialSiteDetails = {
    cmoSites: cmoSites,
    cmoHasMultipleSites: vendor?.cmoHasMultipleSites,
    // cmoHasMultipleSites: true,
    cmoShortName: org.cmoShortFormName || "",
  };

  const initialSitesAndPlantDetails = {
    cmoSites: cmoSites,
    cmoHasMultipleSites: vendor?.cmoHasMultipleSites
  }

  const initialPlantDetails = {
    // plantCode: vendor.plantCode,
    // plantCodeShortName: vendor.plantCodeShortName,
    plantMainAddress: vendor.plantMainAddress || "",
    plantPickupAddress: vendor.plantPickupAddress || "",
    plantDeliveryAddress: vendor.plantDeliveryAddress || "",
    status: vendor.status || "",
    cmoIDFromSponsorERP: vendor.cmoIDFromSponsorERP || "",
  };

  // Function to map cmoBatchShipmentSettingID to the correct batch option string
  const mapBatchSettingIdToOption = (id) => {
    const options = {
      1: "Yes, for batches with the same brand",
      2: "No, single batch only",
      3: "Yes, for batches with same and different brands",
    };
    return options[id] || "No, single batch only"; // Default to "No" if ID is not recognized;
  };

  // Create initialShipmentConfig with proper mapping for batch options
  const initialShipmentConfig = {
    // Map the cmoBatchShipmentSettingID to the correct batch option string
    allowMultipleBatches: mapBatchSettingIdToOption(
      vendor.cmoBatchShipmentSettingID
    ),
    shipmentDuration: vendor.shipmentDuration || "", // Keep as-is initially,
    automateGoodsReceiptProcess: vendor.automateGRP ? "Yes" : "No",
    batchAllocationPdfRequired: vendor.batchAllocationPDFNotificationReqd ? "Yes" : "No",
    pdfFieldTo: vendor.batchAllocationPDFNotificationTo || "CMO Name",
    pdfFieldCC: vendor.batchAllocationPDFNotificationCC || "All the users associated with the CMO, Brand and Product Type",
    pdfFieldSubject: vendor.batchAllocationPDFNotificationSubject || "Batch Allocation done for Purchase Order 'PO Number' with Product 'BMS Product Code', Brand 'Brand Name'.",
    pdfFieldObjective: vendor.batchAllocationPDFNotificationObjective || "The objective of this memo is to notify the 'CMO Name' of allocated (to be consumed) material/component batches for commercial manufacturing of Product 'BMS Product Code', Brand 'Brand name' based on Purchase Order 'PO Number' issued by 'Sponsor Name'.",
    allocationOwnedBySponsor:
      typeof vendor.allocationOwnedBySponsor === "boolean"
        ? vendor.allocationOwnedBySponsor === true
          ? "Yes"
          : "No"
        : "",
    sourceProductDoMEqualsMaterial:
      vendor.domPresentOnDigitizationUI === true
        ? "Post Manufacturing Documents"
        : vendor.domPresentOnDigitizationUI === false
        ? "Inventory"
        : "",
  };

  // Handle both documentTypesList and cmoDocumentTypesList
  const documentTypesList =
    vendor?.cmoDocumentTypesList || vendor?.documentTypesList || [];

  // Remove duplicate document types by ID if any
  const initialDocumentTypes = documentTypesList?.reduce((acc, current) => {
    const x = acc.find(
      (item) => item.documentTypeID === current.documentTypeID
    );
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, []);

  const initialProductTypes =
    vendor?.vendorProductTypeConfigs || vendor?.vendorProductTypeConfigs || [];

  const initialPurchasingDetails = {
    companyCode: vendor.companyCode || "",
    cmoIDFromSponsorERP: vendor.cmoIDFromSponsorERP || "",
    purchasingOrgList: vendor.purchasingOrgList || [],
    purchasingGroupList: vendor.purchasingGroupList || [],
    poDocumentTypesList: vendor.poDocumentTypesList || [],
    receivingPlantIDs: vendor.receivingPlantIDs || [],
    receivingStorageLocationIDs: vendor.receivingStorageLocationIDs || [],
  };

  const initialPlantConfiguration = {
    drugTypeIDList: vendor.drugTypeIDs?.map((item) => item.drugTypeID) || [],
    supplyTypeIDList:
      vendor.supplyTypeIDs?.map((item) => item.supplyTypeID) || [],
    plantTypeIDList: vendor.plantTypeIDs?.map((item) => item.plantTypeID) || [],
    facilityTypeIDList:
      vendor.facilityTypeIDs?.map((item) => item.facilityTypeID) || [],
    // Store the full type objects for the PlantConfigurationSection to use
    _drugTypes: vendor.drugTypeIDs || [],
    _supplyTypes: vendor.supplyTypeIDs || [],
    _plantTypes: vendor.plantTypeIDs || [],
    _facilityTypes: vendor.facilityTypeIDs || [],
  };

  const initialDeliveryAddress = {
    addressLine1: vendor.deliveryAddress?.addressLine1 || "",
    addressLine2: vendor.deliveryAddress?.addressLine2 || "",
    city: vendor.deliveryAddress?.city || "",
    state: vendor.deliveryAddress?.state || "",
    country: vendor.deliveryAddress?.country || "",
    zipCode: vendor.deliveryAddress?.zipCode || "",
  };

  const initialPickupAddress = {
    addressLine1: vendor.pickupAddress?.addressLine1 || "",
    addressLine2: vendor.pickupAddress?.addressLine2 || "",
    city: vendor.pickupAddress?.city || "",
    state: vendor.pickupAddress?.state || "",
    country: vendor.pickupAddress?.country || "",
    zipCode: vendor.pickupAddress?.zipCode || "",
  };

  const initialMainAddress = {
    addressLine1: vendor.mainAddress?.addressLine1 || "",
    addressLine2: vendor.mainAddress?.addressLine2 || "",
    city: vendor.mainAddress?.city || "",
    state: vendor.mainAddress?.state || "",
    country: vendor.mainAddress?.country || "",
    zipCode: vendor.mainAddress?.zipCode || "",
  };

  const initialDocumentFetchDetails = {
    siteNameFromReport: vendor.siteNameFromReport || "",
  };

  const initialIngestionConfig = {
    ingestionConfigurations: vendor.ingestionConfigurations || [],
  };

  const initialPOConfigurations = {
    cmoUsePOInterface: vendor.cmoToUsePOInterface ? "Yes" : "No",
    acceptanceLimitEarlyDelivery:
      vendor.acceptanceLimitForEarlyDeliveryDateChangeByCMO || "N/A",
    acceptanceLimitLaterDelivery:
      vendor.acceptanceLimitForLaterDeliveryDateChangeByCMO || "N/A",
    acceptanceLimitLessQuantity:
      vendor.acceptanceLimitForLessDeliveryQuantityChangeByCMO || "N/A",
    acceptanceLimitMoreQuantity:
      vendor.acceptanceLimitForMoreDeliveryQuantityChangeByCMO || "N/A",
  };

  // Ensure forecast is always set to vendorLevel
  const ensureForecastAtVendorLevel = (mosymphonyDisplayOptions) => {
    if (!mosymphonyDisplayOptions || !Array.isArray(mosymphonyDisplayOptions) || mosymphonyDisplayOptions.length === 0) {
      return mosymphonyDisplayOptions;
    }

    return mosymphonyDisplayOptions.map(option => ({
      ...option,
      objectsToUseVendorLevelData: {
        ...(option.objectsToUseVendorLevelData || {}),
        forecast: true, // Forecast is always at vendor level
      },
      objectsToUseMfgPlantLevelData: {
        ...(option.objectsToUseMfgPlantLevelData || {}),
        forecast: false, // Never at plant level
      },
    }));
  };

  const initialCmoLevelConfig = {
    mosymphonyDisplayOptions: ensureForecastAtVendorLevel(vendor.mosymphonyDisplayOptions),
  };

  // State to track section form data
  const [sectionFormData, setSectionFormData] = useState({
    cmoOrgDetails: initialCmoOrgDetails,
    adminDetails: initialAdminDetails,
    siteDetails: initialSiteDetails,
    plantDetails: initialPlantDetails,
    plantConfiguration: initialPlantConfiguration,
    cmoLevelConfig: initialCmoLevelConfig,
    shipmentConfig: initialShipmentConfig,
    documentTypes: initialDocumentTypes,
    productTypes: initialProductTypes,
    purchasingDetails: initialPurchasingDetails,
    deliveryAddress: initialDeliveryAddress,
    pickupAddress: initialPickupAddress,
    mainAddress: initialMainAddress,
    manufacturingPlantDetails: {},
    documentFetchDetails: initialDocumentFetchDetails,
    poConfigurations: initialPOConfigurations,
    SitesAndPlantDetails: initialSitesAndPlantDetails,
    ingestionConfig: initialIngestionConfig,
  });

  // State to track section payloads
  const [sectionPayloads, setSectionPayloads] = useState({
    cmoOrgDetails: {},
    adminDetails: {},
    siteDetails: {},
    plantDetails: {},
    plantConfiguration: {},
    cmoLevelConfig: {},
    shipmentConfig: {},
    documentTypes: {},
    productTypes: {},
    poConfigurations: {},
    purchasingDetails: {},
    documentFetchDetails: {},
    manufacturingPlantDetails: {},
    ingestionConfig: {},
  });

  // State to track which sections have changed
  const [changedSections, setChangedSections] = useState({});

  // State to track which section is currently being edited
  const [currentlyEditingSection, setCurrentlyEditingSection] = useState(null);

  // State to store all plants' data (addresses, line details, etc.) keyed by manufacturingPlantID
  const [allPlantsData, setAllPlantsData] = useState({});

  // Helper function to get current plant ID
  const getCurrentPlantId = () => {
    return allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID || null;
  };

  // State to hold the final update payload
  const [updatePayload, setUpdatePayload] = useState({
    vendorMosymphonyID: cmoVendorDetails?.vendorMosymphonyID,
    brandOwnerMosymphonyID: cmoVendorDetails?.brandOwnerMosymphonyID,
  });

  // State for notification
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Create refs to access section component methods
  const cmoOrgDetailsRef = useRef(null);
  const adminDetailsRef = useRef(null);
  const plantDetailsRef = useRef(null);
  const plantConfigurationRef = useRef(null);
  const cmoLevelConfigRef = useRef(null);
  const shipmentConfigRef = useRef(null);
  const documentTypesRef = useRef(null);
  const productTypesRef = useRef(null);
  const purchasingDetailsRef = useRef(null);
  const deliveryAddressRef = useRef(null);
  const pickupAddressRef = useRef(null);
  const mainAddressRef = useRef(null);
  // Create refs array for each manufacturing plant tab
  const manufacturingPlantDetailsRefs = useRef([]);
  const documentFetchDetailsRef = useRef(null);
  const poConfigurationsRef = useRef(null);
  const sitesAndPlantsRef = useRef(null);
  const ingestionConfigRef = useRef(null);

  // Update refs array when plants are added/removed
  useEffect(() => {
    // Ensure we have a ref for each plant
    while (manufacturingPlantDetailsRefs.current.length < allManufacturingPlants.length) {
      manufacturingPlantDetailsRefs.current.push(React.createRef());
    }
    // Remove excess refs if plants were deleted
    if (manufacturingPlantDetailsRefs.current.length > allManufacturingPlants.length) {
      manufacturingPlantDetailsRefs.current = manufacturingPlantDetailsRefs.current.slice(0, allManufacturingPlants.length);
    }
  }, [allManufacturingPlants.length]);

  // Fetch data elements from API
  useEffect(() => {
    const fetchDataElements = async () => {
      if (!API_BASE_URL) return;
      
      setIsDataElementsLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/fetchDataElements`
        );
        if (response?.data?.status === 200 && response?.data?.data?.dataElementsList) {
          // Transform flat list into grouped structure by category
          const groupedData = response.data.data.dataElementsList.reduce((acc, element) => {
            const existingCategory = acc.find(item => item.category === element.category);
            if (existingCategory) {
              existingCategory.dataElements.push({
                dataElementId: element.dataElementId,
                dataElementKey: element.dataElementKey,
                dataElementLabel: element.dataElementLabel,
                markForDelete: element.markForDelete
              });
            } else {
              acc.push({
                category: element.category,
                dataElements: [{
                  dataElementId: element.dataElementId,
                  dataElementKey: element.dataElementKey,
                  dataElementLabel: element.dataElementLabel,
                  markForDelete: element.markForDelete
                }]
              });
            }
            return acc;
          }, []);
          setDataElements(groupedData);
        }
      } catch (error) {
        console.error("Error fetching data elements:", error);
      } finally {
        setIsDataElementsLoading(false);
      }
    };

    fetchDataElements();
  }, [API_BASE_URL]);

  // Don't automatically reset changed state when switching plant tabs
  // Let users explicitly save or discard changes
  // useEffect(() => {
  //   // Reset the changed state for manufacturing plant details when tab changes
  //   setChangedSections((prev) => ({
  //     ...prev,
  //     manufacturingPlantDetails: false,
  //   }));
  // }, [selectedPlantTab]);

  // Function to handle section edit mode change
  const handleSectionEditModeChange = (section, isEditing) => {
    // Avoid unnecessary work if the state is already correct
    if (
      (isEditing && currentlyEditingSection === section) ||
      (!isEditing && currentlyEditingSection === null)
    ) {
      return;
    }

    // Use functional update to ensure we're working with the latest state
    setCurrentlyEditingSection((prev) => {
      // If we're trying to enable editing for a section but another section is already being edited
      // This shouldn't happen with the button disabling logic, but this is a safeguard
      if (isEditing && prev !== null && prev !== section) {
        return prev; // Don't change the current section
      }

      return isEditing ? section : null;
    });

    // Check validity for ingestion config when entering edit mode
    if (section === "ingestionConfig" && isEditing) {
      // Use setTimeout to ensure ref is available after state update
      setTimeout(() => {
        if (ingestionConfigRef.current) {
          const isValid = ingestionConfigRef.current.isFormValid();
          setIsIngestionConfigValid(isValid);
        }
      }, 0);
    }
  };

  // Function to handle section data changes
  const handleSectionChange = (
    section,
    hasChanged,
    formData = null,
    payload = null
  ) => {
    // For address sections, manufacturing plant details, and plant configuration, store data by plant ID
    if (
      section === "mainAddress" ||
      section === "pickupAddress" ||
      section === "deliveryAddress" ||
      section === "manufacturingPlantDetails" ||
      section === "plantConfiguration"
    ) {
      if (formData) {
        const plantId = getCurrentPlantId();
        if (plantId) {
          setAllPlantsData((prev) => ({
            ...prev,
            [plantId]: {
              ...prev[plantId],
              [section]: formData,
            },
          }));
        }
      }
    }
    setChangedSections((prev) => ({
      ...prev,
      [section]: hasChanged,
    }));

    // Update form data if provided
    if (formData) {
      setSectionFormData((prev) => ({
        ...prev,
        [section]: formData,
      }));
    }

    // Update payload if provided
    if (payload) {
      setSectionPayloads((prev) => ({
        ...prev,
        [section]: payload,
      }));
    }

    // Check validity for ingestion config
    if (section === "ingestionConfig" && ingestionConfigRef.current) {
      // Use setTimeout to ensure validation happens after child state has been committed
      setTimeout(() => {
        if (ingestionConfigRef.current) {
          const isValid = ingestionConfigRef.current.isFormValid();
          setIsIngestionConfigValid(isValid);
        }
      }, 0);
    }
  };

  // Function to handle section save and update the payload
  const handleSectionSave = (section, data, sectionPayload) => {
    // Update the section form data
    setSectionFormData((prev) => {
      // Special handling for cmoLevelConfig - convert flat structure to nested array structure
      let dataToSave = data;
      if (section === "cmoLevelConfig" && data.mosymphonyDisplayOptions && !Array.isArray(data.mosymphonyDisplayOptions)) {
        // Convert flat structure to nested structure for proper initialization
        const options = data.mosymphonyDisplayOptions;
        dataToSave = {
          mosymphonyDisplayOptions: [{
            objectsToUseVendorLevelData: {
              purchaseOrder: options.purchaseOrders === "vendorLevel",
              batches: options.batches === "vendorLevel",
              inactivePOs: options.inactivePOs === "vendorLevel",
              inventory: options.inventory === "vendorLevel",
              forecast: options.forecast === "vendorLevel",
            },
            objectsToUseMfgPlantLevelData: {
              purchaseOrder: options.purchaseOrders === "plantLevel",
              batches: options.batches === "plantLevel",
              inactivePOs: options.inactivePOs === "plantLevel",
              inventory: options.inventory === "plantLevel",
              forecast: options.forecast === "plantLevel",
            },
          }],
        };
      }
      
      return {
        ...prev,
        [section]: dataToSave,
      };
    });

    // For plant-related sections, also update allPlantsData
    const isPlantRelatedSection = [
      "plantDetails",
      "deliveryAddress",
      "pickupAddress",
      "mainAddress",
      "plantConfiguration",
      "manufacturingPlantDetails",
    ].includes(section);

    if (isPlantRelatedSection) {
      const plantId = getCurrentPlantId();
      if (plantId) {
        setAllPlantsData((prev) => ({
          ...prev,
          [plantId]: {
            ...prev[plantId],
            [section]: data,
          },
        }));
      }
    }

    // Update the section payload
    setSectionPayloads((prev) => ({
      ...prev,
      [section]: sectionPayload,
    }));

    // Update the payload with the section's payload
    setUpdatePayload((prev) => {
      // Create a deep copy of the previous payload
      const mergedPayload = JSON.parse(JSON.stringify(prev));

      // Special handling for vendorPlantDetails to preserve all section changes
      if (sectionPayload.vendorPlantDetails) {
        if (!mergedPayload.vendorPlantDetails) {
          mergedPayload.vendorPlantDetails = {};
        }

        // Merge specific arrays and objects that should be preserved
        const fieldsToMerge = [
          "vendorProductTypeConfigs",
          "cmoDocumentTypesList",
          "plantStorageLocationsList",
          "facilityTypeIDs",
          "drugTypeIDs",
          "plantTypeIDs",
          "supplyTypeIDs",
          "purchasingOrgList",
          "purchasingGroupList",
          "poDocumentTypesList",
          "status", // Include status field to be preserved across section saves
        ];

        // For each specific field that needs special handling
        fieldsToMerge.forEach((field) => {
          if (sectionPayload.vendorPlantDetails[field]) {
            // Update the field with the new section data
            mergedPayload.vendorPlantDetails[field] =
              sectionPayload.vendorPlantDetails[field];

            // Special logging for document types
            if (field === "cmoDocumentTypesList") {
              const activatedCount = mergedPayload.vendorPlantDetails[
                field
              ].filter((item) => item.activated === true).length;
              const renamedCount = mergedPayload.vendorPlantDetails[
                field
              ].filter(
                (item) =>
                  item.newDocumentTypeName && item.newDocumentTypeName !== "NA"
              ).length;
            }
          }
        });

        // Merge other scalar fields
        Object.keys(sectionPayload.vendorPlantDetails).forEach((key) => {
          if (!fieldsToMerge.includes(key)) {
            mergedPayload.vendorPlantDetails[key] =
              sectionPayload.vendorPlantDetails[key];
          }
        });

        // If this is a plant-related section, ensure updatePlantDetailsFlag is set to true
        if (isPlantRelatedSection) {
          mergedPayload.updatePlantDetailsFlag = true;

          // Ensure we have all plant-related data in the payload
          // For each plant-related section, add its data to the merged payload if available
          const plantSections = {
            // plantDetails: ["plantCode", "plantCodeShortName"],
            plantDetails: [],
            deliveryAddress: ["deliveryAddress"],
            pickupAddress: ["pickupAddress"],
            mainAddress: ["mainAddress"],
            plantConfiguration: [
              "drugTypeIDList",
              "supplyTypeIDList",
              "plantTypeIDList",
              "facilityTypeIDList",
            ],
          };

          // Add data from current section payloads for other plant-related sections
          Object.keys(plantSections).forEach((plantSection) => {
            if (
              plantSection !== section &&
              sectionPayloads[plantSection]?.vendorPlantDetails
            ) {
              plantSections[plantSection].forEach((field) => {
                if (
                  sectionPayloads[plantSection].vendorPlantDetails[field] !==
                  undefined
                ) {
                  mergedPayload.vendorPlantDetails[field] =
                    sectionPayloads[plantSection].vendorPlantDetails[field];
                }
              });
            }
          });

          // Also include data from the current section form data
          if (
            section === "plantConfiguration" &&
            plantConfigurationRef.current
          ) {
            const configData = plantConfigurationRef.current.getFormData();
            if (configData) {
              Object.keys(configData).forEach((key) => {
                if (key.endsWith("IDList")) {
                  mergedPayload.vendorPlantDetails[key] = configData[key];
                }
              });
            }
          }
        }
      }

      if (section === "documentFetchDetails") {
        mergedPayload.updateDocumentFetchDetails = true;
        if (!mergedPayload.vendorPlantDetails) {
          mergedPayload.vendorPlantDetails = {};
        }
        mergedPayload.vendorPlantDetails.siteNameFromReport =
          data.siteNameFromReport || "";
      }

      // Preserve update flags from previous state, and add new ones
      const flagFields = [
        "updateCmoOrgDetailsFlag",
        "updateCmoAdminDetailsFlag",
        "updateDocumentTypesFlag",
        "updateDocumentFetchDetails",
        // "updatePlantShipmentConfigsFlag",
        "updateCMOLevelConfigs",
        "updateBatchConfiguration",
        "updatePOConfigFlag",
        "updateStatusFlag", // Add status update flag
        "updatePlantDetailsFlag", // Ensure this flag is preserved
        "updateCMOSiteDetailsFlag", // Add site details update flag
        "updateManufacturingPlantDetailsFlag", // Add manufacturing plant details flag
      ];

      // Copy all top-level properties from sectionPayload except vendorPlantDetails and cmoSites
      Object.keys(sectionPayload).forEach((key) => {
        if (key !== "vendorPlantDetails" && key !== "cmoSites") {
          if (flagFields.includes(key)) {
            // For flag fields, keep them true if either previous or current is true
            mergedPayload[key] =
              prev[key] === true || sectionPayload[key] === true;
          } else {
            mergedPayload[key] = sectionPayload[key];
          }
        }
      });

      // Special handling for cmoSites - merge plant-specific data instead of overwriting
      if (sectionPayload.cmoSites && Array.isArray(sectionPayload.cmoSites)) {
        if (!mergedPayload.cmoSites || mergedPayload.cmoSites.length === 0) {
          // If no existing cmoSites, just copy the new one
          mergedPayload.cmoSites = JSON.parse(JSON.stringify(sectionPayload.cmoSites));
        } else {
          // Create a map of existing sites for quick lookup
          const existingSitesMap = new Map();
          mergedPayload.cmoSites.forEach((site, index) => {
            const siteDetails = site.siteDetails || {};
            const key = siteDetails.collabrixCMOSiteID || siteDetails.siteID;
            if (key) existingSitesMap.set(key, index);
          });

          // Process each site from the new payload
          sectionPayload.cmoSites.forEach((newSite) => {
            const newSiteDetails = newSite.siteDetails || {};
            const siteKey = newSiteDetails.collabrixCMOSiteID || newSiteDetails.siteID;
            const existingSiteIndex = siteKey ? existingSitesMap.get(siteKey) : -1;

            if (existingSiteIndex !== undefined && existingSiteIndex >= 0) {
              // Merge with existing site
              const existingSite = mergedPayload.cmoSites[existingSiteIndex];

              // Update site details (preserve markForDelete if it exists)
              if (newSite.siteDetails) {
                const existingMarkForDelete = existingSite.siteDetails?.markForDelete;
                existingSite.siteDetails = { ...existingSite.siteDetails, ...newSite.siteDetails };
                // Preserve markForDelete flag unless explicitly overridden
                if (existingMarkForDelete !== undefined && newSite.siteDetails.markForDelete === undefined) {
                  existingSite.siteDetails.markForDelete = existingMarkForDelete;
                }
              }

              // Merge plants
              if (newSite.manufacturingPlantDataList && Array.isArray(newSite.manufacturingPlantDataList)) {
                if (!existingSite.manufacturingPlantDataList) {
                  existingSite.manufacturingPlantDataList = [];
                }

                // Create a map of existing plants
                const existingPlantsMap = new Map();
                existingSite.manufacturingPlantDataList.forEach((plant, idx) => {
                  const plantDetails = plant.mfgPlantDetails || plant;
                  const plantKey = plantDetails.collabrixMfgPlantID || plantDetails.mfgPlantID;
                  if (plantKey) existingPlantsMap.set(plantKey, idx);
                });

                // Process each plant from the new payload
                newSite.manufacturingPlantDataList.forEach((newPlant) => {
                  const newPlantDetails = newPlant.mfgPlantDetails || newPlant;
                  const plantKey = newPlantDetails.collabrixMfgPlantID || newPlantDetails.mfgPlantID;
                  const existingPlantIndex = plantKey ? existingPlantsMap.get(plantKey) : undefined;

                  if (existingPlantIndex !== undefined && existingPlantIndex >= 0) {
                    // Merge plant data (addresses, line details, etc.)
                    const existingPlant = existingSite.manufacturingPlantDataList[existingPlantIndex];
                    const existingMarkForDelete = existingPlant.markForDelete || (existingPlant.mfgPlantDetails?.markForDelete);

                    Object.keys(newPlant).forEach((key) => {
                      if (newPlant[key] !== undefined && newPlant[key] !== null) {
                        existingPlant[key] = newPlant[key];
                      }
                    });

                    // Preserve markForDelete flag unless explicitly overridden
                    if (existingMarkForDelete !== undefined) {
                      if (newPlant.markForDelete === undefined) {
                        existingPlant.markForDelete = existingMarkForDelete;
                      }
                      if (newPlant.mfgPlantDetails && newPlant.mfgPlantDetails.markForDelete === undefined) {
                        existingPlant.mfgPlantDetails = existingPlant.mfgPlantDetails || {};
                        existingPlant.mfgPlantDetails.markForDelete = existingMarkForDelete;
                      }
                    }
                  } else {
                    // Add new plant (not in existing list)
                    existingSite.manufacturingPlantDataList.push(newPlant);
                  }
                });
              }
            } else {
              // Add new site (not in existing list)
              mergedPayload.cmoSites.push(newSite);
            }
          });
        }
      }

      // If this is a plant-related section, ensure updatePlantDetailsFlag is set to true
      if (isPlantRelatedSection) {
        mergedPayload.updatePlantDetailsFlag = true;
      }

      return mergedPayload;
    });

    // Mark section as no longer changed
    setChangedSections((prev) => ({
      ...prev,
      [section]: false,
    }));

    // Clear the currently editing section
    setCurrentlyEditingSection(null);

    // Trigger rebuild of manufacturing plants when site details are saved
    if (section === "siteDetails") {
      setSiteDetailsVersion(prev => prev + 1);
    }

    // Show success notification
    // setNotification({
    //   open: true,
    //   message: `${section} updated successfully!`,
    //   severity: "success",
    // });
  };

  // Function to handle section discard
  const handleSectionDiscard = (section) => {
    // Call resetForm on the ref if available
    const sectionRefs = {
      cmoOrgDetails: cmoOrgDetailsRef,
      adminDetails: adminDetailsRef,
      siteDetails: siteDetailsRef,
      plantDetails: plantDetailsRef,
      plantConfiguration: plantConfigurationRef,
      cmoLevelConfig: cmoLevelConfigRef,
      shipmentConfig: shipmentConfigRef,
      documentTypes: documentTypesRef,
      productTypesRef: productTypesRef,
      purchasingDetails: purchasingDetailsRef,
      deliveryAddress: deliveryAddressRef,
      pickupAddress: pickupAddressRef,
      mainAddress: mainAddressRef,
      documentFetchDetails: documentFetchDetailsRef,
      poConfigurations: poConfigurationsRef,
      SitesAndPlantDetails: sitesAndPlantsRef,
      ingestionConfig: ingestionConfigRef,
    };

    // Handle manufacturingPlantDetails specially (it has multiple refs for tabs)
    if (section === "manufacturingPlantDetails") {
      // Reset ALL plant tabs, not just the current one
      manufacturingPlantDetailsRefs.current.forEach((ref) => {
        if (ref && ref.current && ref.current.resetForm) {
          ref.current.resetForm();
        }
      });
    } else if (sectionRefs[section]?.current?.resetForm) {
      sectionRefs[section].current.resetForm();
    } else {
    }

    // For plant-specific sections, clear the modified data from allPlantsData
    if (
      section === "mainAddress" ||
      section === "pickupAddress" ||
      section === "deliveryAddress" ||
      section === "manufacturingPlantDetails" ||
      section === "plantConfiguration"
    ) {
      if (section === "manufacturingPlantDetails") {
        // Clear manufacturingPlantDetails data for ALL plants
        setAllPlantsData((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((plantId) => {
            if (updated[plantId]) {
              const plantData = { ...updated[plantId] };
              delete plantData.manufacturingPlantDetails;
              updated[plantId] = plantData;
            }
          });
          return updated;
        });
      } else {
        // For other plant-specific sections, only clear current plant
        const plantId = getCurrentPlantId();
        if (plantId) {
          setAllPlantsData((prev) => {
            const updated = { ...prev };
            if (updated[plantId]) {
              const plantData = { ...updated[plantId] };
              delete plantData[section];
              updated[plantId] = plantData;
            }
            return updated;
          });
        }
      }

      // Force remount of plant-specific sections to ensure they reload original data
      setPlantSectionResetVersion(prev => prev + 1);
    }

    // Reset the section form data to initial data
    setSectionFormData((prev) => {
      const initialData = {
        cmoOrgDetails: initialCmoOrgDetails,
        adminDetails: initialAdminDetails,
        siteDetails: initialSiteDetails,
        plantDetails: initialPlantDetails,
        plantConfiguration: initialPlantConfiguration,
        cmoLevelConfig: initialCmoLevelConfig,
        shipmentConfig: initialShipmentConfig,
        documentTypes: initialDocumentTypes,
        productTypes: initialProductTypes,
        purchasingDetails: initialPurchasingDetails,
        deliveryAddress: initialDeliveryAddress,
        pickupAddress: initialPickupAddress,
        mainAddress: initialMainAddress,
        documentFetchDetails: initialDocumentFetchDetails,
        poConfigurations: initialPOConfigurations,
        SitesAndPlantDetails: initialSitesAndPlantDetails,
        ingestionConfig: initialIngestionConfig,
      };

      return {
        ...prev,
        [section]: initialData[section],
      };
    });

    setChangedSections((prev) => ({
      ...prev,
      [section]: false,
    }));

    // Clear the currently editing section
    setCurrentlyEditingSection(null);

    // Trigger rebuild of manufacturing plants when site details are discarded
    // Use setTimeout to ensure state updates from resetForm have been applied
    if (section === "siteDetails") {
      setTimeout(() => {
        setSiteDetailsVersion(prev => {
          return prev + 1;
        });
      }, 50); // Delay slightly longer than the onChange setTimeout
    }

    // Show notification
    // setNotification({
    //   open: true,
    //   message: `Changes discarded for ${section}`,
    //   severity: "info",
    // });
  };

  // New function to debug the final payload for verification purposes
  const verifyFinalPayload = () => {
    // Check if any sections still have unsaved changes
    const pendingSections = Object.entries(changedSections)
      .filter(([_, changed]) => changed)
      .map(([section]) => section);

    if (pendingSections.length > 0) {
    }

    // Verify that vendorPlantDetails contains all expected data
    if (updatePayload.vendorPlantDetails) {
      // Check for important arrays
      const arraysToCheck = [
        "cmoDocumentTypesList",
        "plantStorageLocationsList",
        "facilityTypeIDs",
        "drugTypeIDs",
        "plantTypeIDs",
        "supplyTypeIDs",
        "purchasingOrgList",
        "purchasingGroupList",
        "poDocumentTypesList",
      ];

      arraysToCheck.forEach((arrayName) => {
        if (!updatePayload.vendorPlantDetails[arrayName]) {
        } else {
          // For document types, log the activated items to verify changes were preserved
          if (arrayName === "cmoDocumentTypesList") {
            const activatedDocs = updatePayload.vendorPlantDetails[
              arrayName
            ].filter((item) => item.activated === true);

            // Check for renamed document types
            const renamedDocs = updatePayload.vendorPlantDetails[
              arrayName
            ].filter(
              (item) =>
                item.newDocumentTypeName && item.newDocumentTypeName !== "NA"
            );
          }
        }
      });

      // Check important scalar fields
      const importantFields = ["status", "companyCode", "shipmentDuration"];
      importantFields.forEach((field) => {
        if (updatePayload.vendorPlantDetails[field] !== undefined) {
        } else {
        }
      });

      // Check update flags

      const updateFlags = [
        "updatedCmoOrgDetails",
        "updatedCmoAdminDetails",
        "updatedCmoDocumentTypesList",
        "updateDocumentFetchDetails",
        "updatedPlantShipmentConfigs",
        "updatedPOConfigs",
        "updatedPurchasingDetails",
        "updateStatusFlag",
      ];

      updateFlags.forEach((flag) => {
        if (updatePayload[flag] !== undefined) {
        } else if (
          updatePayload.vendorPlantDetails &&
          updatePayload.vendorPlantDetails[flag] !== undefined
        ) {
        } else {
        }
      });
    }

    // Show notification to the user
    // setNotification({
    //   open: true,
    //   message: "Payload verification completed. Check console for details.",
    //   severity: "info",
    // });
  };

  // Function to handle the final update
  const handleUpdate = async () => {
    // Check if any section has changes
    const hasChanges = Object.values(changedSections).some(
      (changed) => changed
    );

    // if (hasChanges) {
    //   setNotification({
    //     open: true,
    //     message: "Please save all section changes before updating",
    //     severity: "warning",
    //   });
    //   return;
    // }

    // Check if any changes have been made to any section
    // if (!hasAnyChanges) {
    //   setNotification({
    //     open: true,
    //     message: "No changes have been made to update",
    //     severity: "info",
    //   });
    //   return;
    // }

    // Prepare the final payload
    const finalPayload = JSON.parse(JSON.stringify(updatePayload));

    // If updatePlantDetailsFlag is true, ensure we have all plant-related data
    if (finalPayload.updatePlantDetailsFlag) {
      if (!finalPayload.vendorPlantDetails) {
        finalPayload.vendorPlantDetails = {};
      }

      // Collect data from all plant-related sections
      const plantSections = {
        plantDetails: plantDetailsRef,
        deliveryAddress: deliveryAddressRef,
        pickupAddress: pickupAddressRef,
        mainAddress: mainAddressRef,
        plantConfiguration: plantConfigurationRef,
        shipmentConfig: shipmentConfigRef,
        poConfigurations: poConfigurationsRef,
      };
      //  console.log("plantSections", plantSections);
      // For each plant-related section, get the current data and add to payload
      Object.entries(plantSections).forEach(([section, ref]) => {
        if (ref.current && ref.current.getPayload) {
          const sectionPayload = ref.current.getPayload();
          if (sectionPayload && sectionPayload.vendorPlantDetails) {
            // Merge the section's vendorPlantDetails into the final payload
            Object.entries(sectionPayload.vendorPlantDetails).forEach(
              ([key, value]) => {
                if (value !== undefined) {
                  finalPayload.vendorPlantDetails[key] = value;
                }
              }
            );
          }
        }
      });
    }

    // Verify that vendorPlantDetails contains all expected data
    if (finalPayload.vendorPlantDetails) {
      // Verify that status field is present in the final payload
      if (finalPayload.vendorPlantDetails.status !== undefined) {
      } else {
      }

      // Check for important arrays
      const arraysToCheck = [
        "cmoDocumentTypesList",
        "plantStorageLocationsList",
        "facilityTypeIDs",
        "drugTypeIDs",
        "plantTypeIDs",
        "supplyTypeIDs",
        "purchasingOrgList",
        "purchasingGroupList",
        "poDocumentTypesList",
      ];

      arraysToCheck.forEach((arrayName) => {
        if (!finalPayload.vendorPlantDetails[arrayName]) {
        } else {
          // For document types, log the activated items to verify changes were preserved
          if (arrayName === "cmoDocumentTypesList") {
            // Check for renamed document types
            const renamedDocs = finalPayload.vendorPlantDetails[
              arrayName
            ].filter(
              (item) =>
                item.newDocumentTypeName && item.newDocumentTypeName !== "NA"
            );

            if (renamedDocs.length > 0) {
            }
          }
        }
      });
    }

    // Check if we should include CMO Level Config in the payload
    // Always include it if: 1) it has unsaved changes, OR 2) there's only 1 site with 1 plant (forced vendor level)
    const shouldIncludeCmoLevelConfig = changedSections.cmoLevelConfig ||
      (currentCmoSites.filter(s => !s.siteDetails?.markForDelete).length === 1 &&
        currentCmoSites.filter(s => !s.siteDetails?.markForDelete)[0]?.manufacturingPlantDataList?.filter(p => !p.markForDelete && !p.mfgPlantDetails?.markForDelete).length === 1);

    if (shouldIncludeCmoLevelConfig && cmoLevelConfigRef.current && cmoLevelConfigRef.current.getPayload) {
      const cmoLevelPayload = cmoLevelConfigRef.current.getPayload();
      if (cmoLevelPayload) {
        // Merge the CMO Level Config payload into the final payload
        if (cmoLevelPayload.vendorPlantDetails) {
          if (!finalPayload.vendorPlantDetails) {
            finalPayload.vendorPlantDetails = {};
          }
          Object.entries(cmoLevelPayload.vendorPlantDetails).forEach(([key, value]) => {
            if (value !== undefined) {
              finalPayload.vendorPlantDetails[key] = value;
            }
          });
        }
        // Also merge the updateCMOLevelConfigs flag
        if (cmoLevelPayload.updateCMOLevelConfigs !== undefined) {
          finalPayload.updateCMOLevelConfigs = cmoLevelPayload.updateCMOLevelConfigs;
        }
      }
    }

    // Build complete cmoSites structure with ALL sites and ALL plants (including markForDelete items)
    if (finalPayload.updatePlantDetailsFlag) {
      const completeCmoSites = [];

      // Get the complete site data including deleted items from siteDetailsRef
      let allSitesData = cmoSites;
      if (siteDetailsRef.current && typeof siteDetailsRef.current.getFormData === 'function') {
        try {
          const currentData = siteDetailsRef.current.getFormData();
          if (currentData?.cmoSites) {
            allSitesData = currentData.cmoSites;
          }
        } catch (error) {
          console.error('Failed to get current site data for payload:', error);
        }
      }

      // Build a map of plant indices from allManufacturingPlants (for non-deleted plants with refs)
      const plantIndexMap = new Map();
      allManufacturingPlants.forEach((plant, plantIndex) => {
        const key = `${plant.collabrixCMOSiteID || plant.siteID}_${plant.collabrixMfgPlantID || plant.manufacturingPlantID}`;
        plantIndexMap.set(key, plantIndex);
      });

      // Build the cmoSites array from ALL sites (including deleted)
      allSitesData.forEach((site) => {
        const siteDetails = site.siteDetails || {};

        const siteData = {
          siteDetails: {
            siteID: siteDetails.siteID,
            siteShortName: siteDetails.siteShortName,
            markForDelete: siteDetails.markForDelete || false,
            collabrixCMOSiteID: siteDetails.collabrixCMOSiteID || undefined,
          },
          manufacturingPlantDataList: [],
        };

        // Process ALL plants for this site (including deleted)
        if (site.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList)) {
          site.manufacturingPlantDataList.forEach((plant) => {
            const plantDetails = plant.mfgPlantDetails || plant;

            // Build unique key to find plant index
            const plantKey = `${plantDetails.collabrixCMOSiteID || siteDetails.collabrixCMOSiteID || siteDetails.siteID}_${plantDetails.collabrixMfgPlantID || plantDetails.mfgPlantID}`;
            const plantIndex = plantIndexMap.get(plantKey);

            // Get line details from the ref if available, otherwise use existing data
            let lineDetailsList = [];

            const plantId = plantDetails.mfgPlantID;



            // Try to get from ref first (for currently rendered plants)
            if (plantIndex !== undefined) {
              const plantRef = manufacturingPlantDetailsRefs.current[plantIndex];
              if (plantRef?.current?.getFormData) {
                const plantFormData = plantRef.current.getFormData();
                lineDetailsList =
                  plantFormData?.lineSuiteDetails?.map((line) => ({
                    lineID: line.lineSuiteID,
                    lineShortName: line.lineSuiteShortName,
                  })) || [];
              }
            }

            // Fallback to multiple sources if not from ref
            if (lineDetailsList.length === 0) {


              // 1. Try allPlantsData first (where saved plant details are stored)
              const savedPlantDetails = allPlantsData[plantId]?.manufacturingPlantDetails;
              if (savedPlantDetails?.lineSuiteDetails && Array.isArray(savedPlantDetails.lineSuiteDetails) && savedPlantDetails.lineSuiteDetails.length > 0) {
                lineDetailsList = savedPlantDetails.lineSuiteDetails.map((line) => ({
                  lineID: line.lineSuiteID || line.lineID,
                  lineShortName: line.lineSuiteShortName || line.lineShortName,
                }));
              }
              // 2. Try plant.lineDetailsList
              else if (plant.lineDetailsList && Array.isArray(plant.lineDetailsList) && plant.lineDetailsList.length > 0) {
                lineDetailsList = plant.lineDetailsList.map((line) => ({
                  lineID: line.lineID,
                  lineShortName: line.lineShortName,
                }));
              }
              // 3. Try plant.lineSuiteDetails
              else if (plant.lineSuiteDetails && Array.isArray(plant.lineSuiteDetails) && plant.lineSuiteDetails.length > 0) {
                lineDetailsList = plant.lineSuiteDetails.map((line) => ({
                  lineID: line.lineSuiteID || line.lineID,
                  lineShortName: line.lineSuiteShortName || line.lineShortName,
                }));
              }
              // 4. Try plantDetails.lineSuiteDetails
              else if (plantDetails.lineSuiteDetails && Array.isArray(plantDetails.lineSuiteDetails) && plantDetails.lineSuiteDetails.length > 0) {
                lineDetailsList = plantDetails.lineSuiteDetails.map((line) => ({
                  lineID: line.lineSuiteID || line.lineID,
                  lineShortName: line.lineSuiteShortName || line.lineShortName,
                }));
              }
            }


            // Get address data from stored state or original data
            // Use plant ID instead of index to avoid data leakage on deletion
            const mainAddress =
              (plantId ? allPlantsData[plantId]?.mainAddress : null) ||
              plant.mainAddress ||
              {};
            const pickupAddress =
              (plantId ? allPlantsData[plantId]?.pickupAddress : null) ||
              plant.pickupAddress ||
              {};
            const deliveryAddress =
              (plantId ? allPlantsData[plantId]?.deliveryAddress : null) ||
              plant.deliveryAddress ||
              {};

            const plantData = {
              mfgPlantDetails: {
                mfgPlantID: plantDetails.mfgPlantID,
                mfgPlantShortName: plantDetails.mfgPlantShortName,
                aslNumber: plantDetails.aslNumber || null,
                markForDelete: plant.markForDelete || plantDetails.markForDelete || false,
                collabrixMfgPlantID: plantDetails.collabrixMfgPlantID || undefined,
                collabrixCMOSiteID: plantDetails.collabrixCMOSiteID || siteDetails.collabrixCMOSiteID || undefined,
              },
              lineDetailsList,
              mainAddress,
              pickupAddress,
              deliveryAddress,
            };

            // Get plant configuration data - use plant data which has the correct format
            // Use plant ID instead of index to avoid data leakage on deletion
            const plantConfig = (plantId ? allPlantsData[plantId]?.plantConfiguration : null) || {};

            // Helper function to extract IDs from arrays that might contain objects or plain IDs
            const extractIDs = (list, idKey) => {
              if (!Array.isArray(list) || list.length === 0) return [];
              return list.map(item => {
                if (typeof item === 'object' && item !== null) {
                  return item[idKey];
                }
                return item;
              }).filter(id => id !== undefined && id !== null);
            };

            // Use plant data from site data or saved config, extracting only IDs
            const plantTypeIDList = extractIDs(plantConfig.plantTypeIDList || plant.plantTypeIDList || [], 'plantTypeID');
            const drugTypeIDList = extractIDs(plantConfig.drugTypeIDList || plant.drugTypeIDList || [], 'drugTypeID');
            const supplyTypeIDList = extractIDs(plantConfig.supplyTypeIDList || plant.supplyTypeIDList || [], 'supplyTypeID');
            const facilityTypeIDList = extractIDs(plantConfig.facilityTypeIDList || plant.facilityTypeIDList || [], 'facilityTypeID');

            // Add plant type lists if available
            if (plantTypeIDList.length > 0) {
              plantData.plantTypeIDList = plantTypeIDList;
            }
            if (drugTypeIDList.length > 0) {
              plantData.drugTypeIDList = drugTypeIDList;
            }
            if (supplyTypeIDList.length > 0) {
              plantData.supplyTypeIDList = supplyTypeIDList;
            }
            if (facilityTypeIDList.length > 0) {
              plantData.facilityTypeIDList = facilityTypeIDList;
            }

            // BCM-3PL: Freight Forwarder Details on UPDATE (one FF per MFG plant).
            // Add-CMO (PlantDetails.jsx) already sends this; the update flow did not.
            // Send ONLY the FF block here (other plant fields owned elsewhere). Includes
            // the existing collabrixFreightForwarderID so the MW UPDATES (does not
            // regenerate) the record. Guarded so it's a no-op until FF data is present.
            // Prefer the edited value from the plant section (stored under
            // manufacturingPlantDetails), then any top-level edited value, then the
            // raw plant, then normalize the API response (`freightForwardersDetails`)
            // so existing FF round-trips on save.
            const responseFFD = plant.freightForwardersDetails || plant.freightForwarderDetails;
            const normalizedResponseFF = responseFFD
              ? {
                  id: responseFFD.freightForwarderID || '',
                  name: responseFFD.freightForwarderName || '',
                  portal: responseFFD.portal || '',
                  billingCenter:
                    responseFFD.billingCentreForShipment || responseFFD.billingCenterForShipment || '',
                  collabrixFreightForwarderID:
                    responseFFD.collabribrixFreightForwarderID || responseFFD.collabrixFreightForwarderID || null,
                }
              : null;
            const ff =
              (plantId ? allPlantsData[plantId]?.manufacturingPlantDetails?.freightForwarder : null) ||
              (plantId ? allPlantsData[plantId]?.freightForwarder : null) ||
              plant.freightForwarder ||
              normalizedResponseFF ||
              {};
            const ffHasData = [ff.id, ff.name, ff.portal, ff.billingCenter].some(
              (v) => v !== undefined && v !== null && String(v).trim() !== ''
            );
            if (ffHasData) {
              plantData.freightForwarderDetails = {
                ...(ff.collabrixFreightForwarderID
                  ? { collabrixFreightForwarderID: ff.collabrixFreightForwarderID }
                  : {}),
                freightForwarderID: (ff.id ?? '').toString().trim(),
                freightForwarderName: (ff.name ?? '').toString().trim(),
                portal: (ff.portal ?? '').toString().trim(),
                billingCenterForShipment: (ff.billingCenter ?? '').toString().trim(),
                markForDelete: false,
              };
            }

            siteData.manufacturingPlantDataList.push(plantData);
          });
        }

        completeCmoSites.push(siteData);
      });

      // Add the complete cmoSites to the payload
      finalPayload.cmoSites = completeCmoSites;
    }

    // console.log("Final payload ready for submission:", finalPayload);
    // return

    // console.log("Complete cmoSites structure:", JSON.stringify(finalPayload.cmoSites, null, 2));

    try {
      // Set loading state to true before API call
      setIsUpdating(true);
      const response = await axios.post(
        `${API_BASE_URL}/updateVendorPlantDetails`,
        finalPayload
      );

      // Handle the response
      if (response.data.status) {
        // Show success popup instead of notification
        setIsUpdating(false);

        setShowSuccessPopup(true);
      } else {
        setNotification({
          open: true,
          message:
            response.data.message ||
            "Failed to update CMO configuration. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Failed to update CMO configuration:", error);
      setNotification({
        open: true,
        message: `Failed to update CMO configuration: ${error.message || "Unknown error"
          }`,
        severity: "error",
      });
      // Reset loading state on error
      setIsUpdating(false);
    } finally {
      // In case we need additional cleanup logic in the future
      // Currently, we don't set isUpdating to false on success because we want the loader to remain
      // visible until the success popup is displayed and the user clicks OK
    }
  };

  // Check if any section has unsaved changes
  // Exclude cmoLevelConfig since it's automatically included in the payload during handleUpdate
  const hasUnsavedChanges = Object.entries(changedSections)
    .filter(([section, _]) => section !== "cmoLevelConfig")
    .some(([_, changed]) => changed);

  // Check if any changes have been made to any section
  // Include cmoLevelConfig changes even if not saved
  const hasAnyChanges = Object.keys(updatePayload).length > 2 || changedSections.cmoLevelConfig; // More than just the IDs

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Handle popup OK button click
  const handlePopupOk = () => {
    // Reset update loading state when the user clicks OK
    setIsUpdating(false);
    // Set redirecting state and navigate back
    setIsRedirecting(true);
    setTimeout(() => {
      history.goBack();
    }, 500);
  };

  const handleGoBack = () => history.goBack();

  return (
    <Paper>
      <Box className={classes.stepperHeader}>
        <Typography className={classes.heading} variant="h5">
          Update CMO Details
        </Typography>
        <Typography className={classes.subheading}>
          Please update the CMO details and configurations in the fields below.
        </Typography>
        <Box className={`close-page-action`} onClick={handleGoBack}>
          <Typography>Cancel</Typography>
          <CloseIcon />
        </Box>
      </Box>

      <div
        className="update-cmo-details view-cmo-details-content"
        // style={{ padding: "0 32px 50px 32px"    }}
        style={{
          padding: "0 32px 50px 32px",
          flexGrow: 1,
        }}
      >
        {/* CMO Details Section with centralized control buttons */}
        <div
          className={classes.sectionHeader}
          style={{ overflowY: "auto", maxHeight: "400px" }}
        >
          <Typography variant="h6" className="section-title">
            CMO Details
          </Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "cmoOrgDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("cmoOrgDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (cmoOrgDetailsRef.current) {
                      // Validate form first
                      if (cmoOrgDetailsRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = cmoOrgDetailsRef.current.getFormData();
                        const payload = cmoOrgDetailsRef.current.getPayload();
                        handleSectionSave("cmoOrgDetails", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["cmoOrgDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() => {
                  // First set the parent's state directly
                  setCurrentlyEditingSection("cmoOrgDetails");

                  // Only call the handler after the state is updated
                  setTimeout(() => {
                    if (currentlyEditingSection !== "cmoOrgDetails") {
                      handleSectionEditModeChange("cmoOrgDetails", true);
                    }
                  }, 50);
                }}
                // color="primary"
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "cmoOrgDetails"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>

        <MemoizedCMODetailsSection
          ref={cmoOrgDetailsRef}
          key={`cmo-details-${currentlyEditingSection === "cmoOrgDetails"}`}
          initialData={sectionFormData.cmoOrgDetails}
          onChange={(hasChanged) =>
            handleSectionChange("cmoOrgDetails", hasChanged)
          }
          onSave={(data, payload) =>
            handleSectionSave("cmoOrgDetails", data, payload)
          }
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          saasUserId={org?.saasUserId}
          editMode={currentlyEditingSection === "cmoOrgDetails"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("cmoOrgDetails", isEditing)
          }
          onDiscard={() => handleSectionDiscard("cmoOrgDetails")}
          disableEdit={true} // We're handling edit state in the parent now
        />

        {/* Admin Details Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6" className="section-title">
            Admin Details
          </Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "adminDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("adminDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (adminDetailsRef.current) {
                      // Validate form first
                      if (adminDetailsRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = adminDetailsRef.current.getFormData();
                        const payload = adminDetailsRef.current.getPayload();
                        handleSectionSave("adminDetails", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["adminDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() => {
                  // First set the parent's state directly
                  setCurrentlyEditingSection("adminDetails");

                  // Only call the handler after the state is updated
                  setTimeout(() => {
                    if (currentlyEditingSection !== "adminDetails") {
                      handleSectionEditModeChange("adminDetails", true);
                    }
                  }, 50);
                }}
                color="primary"
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "adminDetails"
                }
                className={classes.iconButtonStyle}
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedAdminDetailsSection
          ref={adminDetailsRef}
          key={`admin-details-${currentlyEditingSection === "adminDetails"}`}
          initialData={sectionFormData.adminDetails}
          onChange={(data) => {
            handleSectionChange("adminDetails", true);
          }}
          onSave={(data, payload) =>
            handleSectionSave("adminDetails", data, payload)
          }
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          saasUserId={org?.saasUserId}
          editMode={currentlyEditingSection === "adminDetails"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("adminDetails", isEditing)
          }
          onDiscard={() => handleSectionDiscard("adminDetails")}
          disableEdit={true} // We're handling edit state in the parent now
        />

        {/* Site Details Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Site Details</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "siteDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("siteDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (siteDetailsRef.current) {
                      // Validate form first
                      if (siteDetailsRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = siteDetailsRef.current.getFormData();
                        const payload = siteDetailsRef.current.getPayload();
                        handleSectionSave("siteDetails", data, payload);

                        // Note: CMO Level Config is no longer automatically reset when sites are saved
                        // Users should manually configure CMO Level settings as needed
                      }
                    }
                  }}
                  disabled={!changedSections["siteDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() => {
                  // First set the parent's state directly
                  setCurrentlyEditingSection("siteDetails");

                  // Only call the handler after the state is updated
                  setTimeout(() => {
                    if (currentlyEditingSection !== "siteDetails") {
                      handleSectionEditModeChange("siteDetails", true);
                    }
                  }, 50);
                }}
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "siteDetails"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedSiteDetailsSection
          ref={siteDetailsRef}
          key={`site-details-${currentlyEditingSection === "siteDetails"}`}
          initialData={sectionFormData.siteDetails}
          originalSiteDetails={{
            cmoSites: cmoSites,
            // cmoHasMultipleSites: true,
            cmoHasMultipleSites: vendor?.cmoHasMultipleSites
          }}
          onChange={(hasChanged) => {
            handleSectionChange("siteDetails", hasChanged);
          }}
          // onSiteCountChange={(count) => {
          //   // When site count becomes 1, reset CMO Level Config to vendor level
          //   if (count === 1 && cmoLevelConfigRef.current) {
          //     cmoLevelConfigRef.current.resetToVendorLevel();
          //   }
          // }}
          // onSave={(data, payload) =>
          //   handleSectionSave("siteDetails", data, payload)
          // }
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          editMode={currentlyEditingSection === "siteDetails"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("siteDetails", isEditing)
          }
        // onDiscard={() => handleSectionDiscard("siteDetails")}
        // disableEdit={true} // We're handling edit state in the parent now
        />

        {/* Manufacturing Plant Details Section with Tabs */}
        <div className={classes.sectionHeader}>
          <Box>
            <Typography variant="h6">Manufacturing Plant Details</Typography>
          </Box>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "manufacturingPlantDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("manufacturingPlantDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    const currentRef = manufacturingPlantDetailsRefs.current[selectedPlantTab];
                    if (currentRef && currentRef.current) {
                      // Validate form first
                      if (currentRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = currentRef.current.getFormData();
                        const payload = currentRef.current.getPayload();
                        handleSectionSave("manufacturingPlantDetails", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["manufacturingPlantDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("manufacturingPlantDetails", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  !hasValidSites ||
                  (currentlyEditingSection !== null &&
                    currentlyEditingSection !== "manufacturingPlantDetails")
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>

        {/* Show message if no site details are available */}
        {!hasValidSites ? (
          <>
            <Box
              style={{
                padding: '20px',
                margin: '20px auto',
                maxWidth: '1270px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            >
              <Typography
                variant="body1"
                style={{
                  color: '#856404',
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                Please add Site Details first to configure Manufacturing Plant Details, Address sections, and Manufacturing Facility Configuration.
              </Typography>
            </Box>
            <Divider style={{ margin: '20px auto', maxWidth: '1270px' }} />
          </>
        ) : (
          <>
            {/* Plant Details Section with centralized control buttons */}
            {/* <div className={classes.sectionHeader}>
          <Typography variant="h6">Plant Details</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "plantDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("plantDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (plantDetailsRef.current) {
                      // Validate form first
                      if (plantDetailsRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = plantDetailsRef.current.getFormData();
                        const payload = plantDetailsRef.current.getPayload();
                        handleSectionSave("plantDetails", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["plantDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("plantDetails", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "plantDetails"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedPlantDetailsSection
          ref={plantDetailsRef}
          key={`plant-details-${currentlyEditingSection === "plantDetails"}`}
          initialData={sectionFormData.plantDetails}
          onChange={(data, hasChanged) =>
            handleSectionChange("plantDetails", hasChanged)
          }
          onSave={(data, payload) =>
            handleSectionSave("plantDetails", data, payload)
          }
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          editMode={currentlyEditingSection === "plantDetails"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("plantDetails", isEditing)
          }
          onDiscard={() => handleSectionDiscard("plantDetails")}
          disableEdit={true} // We're handling edit state in the parent now
        /> */}

            {/* Tabs for each Manufacturing Plant */}
            <div className="plant-details-step" style={{ maxWidth: '1270px', margin: '0 auto' }}>
              {allManufacturingPlants.length > 0 && (
                <Tabs
                  value={selectedPlantTab}
                  onChange={(event, newValue) => setSelectedPlantTab(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  style={{ marginBottom: 16 }}
                >
                  {allManufacturingPlants.map((plant, index) => (
                    <Tab
                      key={index}
                      label={plant.manufacturingPlantShortName}
                      id={`plant-tab-${index}`}
                      aria-controls={`plant-tabpanel-${index}`}
                    />
                  ))}
                </Tabs>
              )}
            </div>

            {/* Tab Panels for each Manufacturing Plant */}
            {allManufacturingPlants.map((plant, index) => (
              <div
                key={index}
                role="tabpanel"
                hidden={selectedPlantTab !== index}
                id={`plant-tabpanel-${index}`}
                aria-labelledby={`plant-tab-${index}`}
              >
                {selectedPlantTab === index && (
                  <MemoizedManufacturingPlantDetailsSection
                    ref={manufacturingPlantDetailsRefs.current[index]}
                    key={`manufacturing-plant-details-${index}-${currentlyEditingSection === "manufacturingPlantDetails" ? "edit" : "readonly"}-v${plantSectionResetVersion}`}
                    initialData={allPlantsData[plant.manufacturingPlantID]?.manufacturingPlantDetails || plant}
                    onChange={(data, hasChanged) =>
                      handleSectionChange("manufacturingPlantDetails", hasChanged, data)
                    }
                    onSave={(data, payload) =>
                      handleSectionSave("manufacturingPlantDetails", data, payload)
                    }
                    vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
                    brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
                    editMode={currentlyEditingSection === "manufacturingPlantDetails"}
                    onEditModeChange={(isEditing) =>
                      handleSectionEditModeChange("manufacturingPlantDetails", isEditing)
                    }
                    onDiscard={() => handleSectionDiscard("manufacturingPlantDetails")}
                    disableEdit={false}
                  />
                )}
              </div>
            ))}

            {/* Main Address Section with centralized control buttons */}
            <div className={classes.sectionHeader}>
              <Box>
                <Typography variant="h6">Main Address</Typography>
                {allManufacturingPlants.length > 0 && (
                  <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                    For Plant: {allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName}
                  </Typography>
                )}
              </Box>
              <Box
                className="section-actions"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {currentlyEditingSection === "mainAddress" ? (
                  <Box className={classes.actionButtons}>
                    <Button
                      variant="outlined"
                      className={classes.discardButton}
                      onClick={() => handleSectionDiscard("mainAddress")}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      className={classes.saveButton}
                      onClick={() => {
                        if (mainAddressRef.current) {
                          // Validate form first
                          if (mainAddressRef.current.validateForm()) {
                            // Get the form data and payload
                            const data = mainAddressRef.current.getFormData();
                            const payload = mainAddressRef.current.getPayload();
                            handleSectionSave("mainAddress", data, payload);
                          }
                        }
                      }}
                      disabled={!changedSections["mainAddress"]}
                    >
                      Save Changes
                    </Button>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() => handleSectionEditModeChange("mainAddress", true)}
                    className={classes.iconButtonStyle}
                    disabled={
                      currentlyEditingSection !== null &&
                      currentlyEditingSection !== "mainAddress"
                    }
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </div>
            <MemoizedMainAddressSection
              ref={mainAddressRef}
              key={`main-address-tab-${selectedPlantTab}-v${plantSectionResetVersion}`}
              selectedPlantTab={selectedPlantTab}
              plantInfo={allManufacturingPlants.length > 0 ? {
                siteID: allManufacturingPlants[selectedPlantTab]?.siteID,
                siteShortName: allManufacturingPlants[selectedPlantTab]?.siteShortName,
                manufacturingPlantID: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID,
                manufacturingPlantShortName: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName,
              } : null}
              initialData={allManufacturingPlants.length > 0 ? (allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.mainAddress || allManufacturingPlants[selectedPlantTab]?.mainAddress) : sectionFormData.mainAddress}
              // <MemoizedDeliveryAddressSection
              //   ref={deliveryAddressRef}
              //   key={`delivery-address-${currentlyEditingSection === "deliveryAddress"
              //     }`}
              //   initialData={sectionFormData.deliveryAddress}
              onChange={(data, hasChanged) =>
                handleSectionChange("mainAddress", hasChanged, data)
              }
              onSave={(data, payload) =>
                handleSectionSave("mainAddress", data, payload)
              }
              vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
              brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
              editMode={currentlyEditingSection === "mainAddress"}
              onEditModeChange={(isEditing) =>
                handleSectionEditModeChange("mainAddress", isEditing)
              }
              onDiscard={() => handleSectionDiscard("mainAddress")}
              disableEdit={true} // We're handling edit state in the parent now
            />

            {/* Pickup Address Section with centralized control buttons */}
            <div className={classes.sectionHeader}>
              <Box>
                <Typography variant="h6">Pickup Address</Typography>
                {allManufacturingPlants.length > 0 && (
                  <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                    For Plant: {allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName}
                  </Typography>
                )}
              </Box>
              <Box
                className="section-actions"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {currentlyEditingSection === "pickupAddress" ? (
                  <Box className={classes.actionButtons}>
                    <Button
                      variant="outlined"
                      className={classes.discardButton}
                      onClick={() => handleSectionDiscard("pickupAddress")}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      className={classes.saveButton}
                      onClick={() => {
                        if (pickupAddressRef.current) {
                          // Validate form first
                          if (pickupAddressRef.current.validateForm()) {
                            // Get the form data and payload
                            const data = pickupAddressRef.current.getFormData();
                            const payload = pickupAddressRef.current.getPayload();
                            handleSectionSave("pickupAddress", data, payload);
                          }
                        }
                      }}
                      disabled={!changedSections["pickupAddress"]}
                    >
                      Save Changes
                    </Button>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() =>
                      handleSectionEditModeChange("pickupAddress", true)
                    }
                    className={classes.iconButtonStyle}
                    disabled={
                      currentlyEditingSection !== null &&
                      currentlyEditingSection !== "pickupAddress"
                    }
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </div>
            <MemoizedPickupAddressSection
              ref={pickupAddressRef}
              key={`pickup-address-tab-${selectedPlantTab}-v${plantSectionResetVersion}`}
              selectedPlantTab={selectedPlantTab}
              plantInfo={allManufacturingPlants.length > 0 ? {
                siteID: allManufacturingPlants[selectedPlantTab]?.siteID,
                siteShortName: allManufacturingPlants[selectedPlantTab]?.siteShortName,
                manufacturingPlantID: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID,
                manufacturingPlantShortName: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName,
              } : null}
              initialData={allManufacturingPlants.length > 0 ? (allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.pickupAddress || allManufacturingPlants[selectedPlantTab]?.pickupAddress) : sectionFormData.pickupAddress}
              onChange={(data, hasChanged) =>
                handleSectionChange("pickupAddress", hasChanged, data)
              }
              onSave={(data, payload) =>
                handleSectionSave("pickupAddress", data, payload)
              }
              vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
              brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
              editMode={currentlyEditingSection === "pickupAddress"}
              onEditModeChange={(isEditing) =>
                handleSectionEditModeChange("pickupAddress", isEditing)
              }
              onDiscard={() => handleSectionDiscard("pickupAddress")}
              disableEdit={true} // We're handling edit state in the parent now
            />

            {/* Delivery Address Section with centralized control buttons */}
            <div className={classes.sectionHeader}>
              <Box>
                <Typography variant="h6">Delivery Address</Typography>
                {allManufacturingPlants.length > 0 && (
                  <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                    For Plant: {allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName}
                  </Typography>
                )}
              </Box>
              <Box
                className="section-actions"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {currentlyEditingSection === "deliveryAddress" ? (
                  <Box className={classes.actionButtons}>
                    <Button
                      variant="outlined"
                      className={classes.discardButton}
                      onClick={() => handleSectionDiscard("deliveryAddress")}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      className={classes.saveButton}
                      onClick={() => {
                        if (deliveryAddressRef.current) {
                          // Validate form first
                          if (deliveryAddressRef.current.validateForm()) {
                            // Get the form data and payload
                            const data = deliveryAddressRef.current.getFormData();
                            const payload = deliveryAddressRef.current.getPayload();
                            handleSectionSave("deliveryAddress", data, payload);
                          }
                        }
                      }}
                      disabled={!changedSections["deliveryAddress"]}
                    >
                      Save Changes
                    </Button>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() =>
                      handleSectionEditModeChange("deliveryAddress", true)
                    }
                    className={classes.iconButtonStyle}
                    disabled={
                      currentlyEditingSection !== null &&
                      currentlyEditingSection !== "deliveryAddress"
                    }
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </div>
            <MemoizedDeliveryAddressSection
              ref={deliveryAddressRef}
              key={`delivery-address-tab-${selectedPlantTab}-v${plantSectionResetVersion}`}
              selectedPlantTab={selectedPlantTab}
              plantInfo={allManufacturingPlants.length > 0 ? {
                siteID: allManufacturingPlants[selectedPlantTab]?.siteID,
                siteShortName: allManufacturingPlants[selectedPlantTab]?.siteShortName,
                manufacturingPlantID: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID,
                manufacturingPlantShortName: allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName,
              } : null}
              initialData={allManufacturingPlants.length > 0 ? (allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.deliveryAddress || allManufacturingPlants[selectedPlantTab]?.deliveryAddress) : sectionFormData.deliveryAddress}
              onChange={(data, hasChanged) =>
                handleSectionChange("deliveryAddress", hasChanged, data)
              }
              onSave={(data, payload) =>
                handleSectionSave("deliveryAddress", data, payload)
              }
              vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
              brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
              editMode={currentlyEditingSection === "deliveryAddress"}
              onEditModeChange={(isEditing) =>
                handleSectionEditModeChange("deliveryAddress", isEditing)
              }
              onDiscard={() => handleSectionDiscard("deliveryAddress")}
              disableEdit={true} // We're handling edit state in the parent now
            />

            {/* Manufacturing Facility Configuration Section with centralized control buttons */}
            <div className={classes.sectionHeader}>
              <Box>
                <Typography variant="h6">
                  Manufacturing Facility Configuration
                </Typography>
                {allManufacturingPlants.length > 0 && (
                  <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                    For Plant: {allManufacturingPlants[selectedPlantTab]?.manufacturingPlantShortName}
                  </Typography>
                )}
              </Box>
              <Box
                className="section-actions"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {currentlyEditingSection === "plantConfiguration" ? (
                  <Box className={classes.actionButtons}>
                    <Button
                      variant="outlined"
                      className={classes.discardButton}
                      onClick={() => handleSectionDiscard("plantConfiguration")}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      className={classes.saveButton}
                      onClick={() => {
                        if (plantConfigurationRef.current) {
                          // Validate form first
                          if (plantConfigurationRef.current.validateForm) {
                            if (plantConfigurationRef.current.validateForm()) {
                              // Get the form data and payload
                              const data =
                                plantConfigurationRef.current.getFormData();
                              const payload =
                                plantConfigurationRef.current.getPayload();
                              handleSectionSave(
                                "plantConfiguration",
                                data,
                                payload
                              );
                            }
                          } else {
                            // If no validation function, just save
                            const data = plantConfigurationRef.current.getFormData
                              ? plantConfigurationRef.current.getFormData()
                              : sectionFormData.plantConfiguration;
                            const payload = plantConfigurationRef.current.getPayload
                              ? plantConfigurationRef.current.getPayload()
                              : {
                                vendorPlantDetails:
                                  sectionFormData.plantConfiguration,
                              };
                            handleSectionSave("plantConfiguration", data, payload);
                          }
                        }
                      }}
                      disabled={!changedSections["plantConfiguration"]}
                    >
                      Save Changes
                    </Button>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() =>
                      handleSectionEditModeChange("plantConfiguration", true)
                    }
                    className={classes.iconButtonStyle}
                    disabled={
                      currentlyEditingSection !== null &&
                      currentlyEditingSection !== "plantConfiguration"
                    }
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </div>
            <MemoizedPlantConfigurationSection
              ref={plantConfigurationRef}
              key={`plant-configuration-tab-${selectedPlantTab}-${currentlyEditingSection === "plantConfiguration" ? "edit" : "readonly"}-v${plantSectionResetVersion}`}
              formData={allManufacturingPlants.length > 0 ? {
                // Prioritize saved data from allPlantsData over original data from allManufacturingPlants
                drugTypeIDList: allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.plantConfiguration?.drugTypeIDList || allManufacturingPlants[selectedPlantTab]?.drugTypeIDList || [],
                supplyTypeIDList: allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.plantConfiguration?.supplyTypeIDList || allManufacturingPlants[selectedPlantTab]?.supplyTypeIDList || [],
                plantTypeIDList: allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.plantConfiguration?.plantTypeIDList || allManufacturingPlants[selectedPlantTab]?.plantTypeIDList || [],
                facilityTypeIDList: allPlantsData[allManufacturingPlants[selectedPlantTab]?.manufacturingPlantID]?.plantConfiguration?.facilityTypeIDList || allManufacturingPlants[selectedPlantTab]?.facilityTypeIDList || [],
              } : sectionFormData.plantConfiguration}
              readOnly={currentlyEditingSection !== "plantConfiguration"}
              vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
              brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
              onFieldChange={(field, value) => {
                // Store plant-specific configuration data
                // Prioritize currently saved data from allPlantsData over original data
                const plantId = getCurrentPlantId();
                const currentData = plantId ? allPlantsData[plantId]?.plantConfiguration || {} : {};
                const originalData = allManufacturingPlants[selectedPlantTab] || {};

                const plantConfigData = {
                  drugTypeIDList: currentData.drugTypeIDList || originalData.drugTypeIDList || [],
                  supplyTypeIDList: currentData.supplyTypeIDList || originalData.supplyTypeIDList || [],
                  plantTypeIDList: currentData.plantTypeIDList || originalData.plantTypeIDList || [],
                  facilityTypeIDList: currentData.facilityTypeIDList || originalData.facilityTypeIDList || [],
                  [field]: value,
                };

                // Store in allPlantsData for the current plant tab
                handleSectionChange("plantConfiguration", true, plantConfigData);

                // Mark as changed
                setChangedSections((prev) => ({
                  ...prev,
                  plantConfiguration: true,
                }));
              }}
            />
          </>
        )}

        {/* CMO Level Configurations Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">CMO Level Configurations</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "cmoLevelConfig" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("cmoLevelConfig")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (cmoLevelConfigRef.current) {
                      const payload = cmoLevelConfigRef.current.getPayload();
                      const currentFormData = cmoLevelConfigRef.current.getFormData();
                      handleSectionSave(
                        "cmoLevelConfig",
                        currentFormData,
                        payload
                      );
                    }
                  }}
                  disabled={(!changedSections["cmoLevelConfig"] && currentCmoSites.filter(
                    (site) => !site?.siteDetails?.markForDelete
                  ).length > 1) || (!currentCmoSites.filter(
                    (site) => !site?.siteDetails?.markForDelete
                  ).length === 1)}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("cmoLevelConfig", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "cmoLevelConfig"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>

        <MemoizedCmoLevelConfigSection
          ref={cmoLevelConfigRef}
          key={`cmo-level-config-${currentlyEditingSection === "cmoLevelConfig"}`}
          initialData={sectionFormData.cmoLevelConfig}
          onChange={(hasChanged, formData) =>
            handleSectionChange("cmoLevelConfig", hasChanged, formData)
          }
          onSave={(data, payload) =>
            handleSectionSave("cmoLevelConfig", data, payload)
          }
          editMode={currentlyEditingSection === "cmoLevelConfig"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("cmoLevelConfig", isEditing)
          }
          onDiscard={() => handleSectionDiscard("cmoLevelConfig")}
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          disableEdit={true}
          cmoSites={currentCmoSites}
        />

        {/* Shipment Config Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Batch Configuration</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "shipmentConfig" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("shipmentConfig")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (shipmentConfigRef.current) {
                      // Validate form first
                      if (shipmentConfigRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = shipmentConfigRef.current.getFormData();
                        const payload = shipmentConfigRef.current.getPayload();
                        handleSectionSave("shipmentConfig", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["shipmentConfig"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("shipmentConfig", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "shipmentConfig"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedShipmentConfigSection
          ref={shipmentConfigRef}
          key={`shipment-config-${currentlyEditingSection === "shipmentConfig"
            }`}
          form={sectionFormData.shipmentConfig}
          errors={{}}
          editMode={currentlyEditingSection === "shipmentConfig"}
          onChange={(e) => {
            if (e.target.name === "formChanged") {
              handleSectionChange("shipmentConfig", e.target.value);
            } else {
              // Directly update any field in shipmentConfig form data
              const newValue = e.target.value;
              const fieldName = e.target.name;

              // Update form data
              setSectionFormData((prev) => ({
                ...prev,
                shipmentConfig: {
                  ...prev.shipmentConfig,
                  [fieldName]: newValue,
                },
              }));

              // Mark the section as changed to enable the save button
              setChangedSections((prev) => ({
                ...prev,
                shipmentConfig: true,
              }));
            }
            // else if (e.target.name === "allocationOwnedBySponsor") {
            //   // Add this new handler for allocationOwnedBySponsor
            //   const newValue = e.target.value;

            //   // Update form data
            //   setSectionFormData((prev) => ({
            //     ...prev,
            //     shipmentConfig: {
            //       ...prev.shipmentConfig,
            //       allocationOwnedBySponsor: newValue,
            //     },
            //   }));

            //   // Mark the section as changed to enable the save button
            //   setChangedSections((prev) => ({
            //     ...prev,
            //     shipmentConfig: true,
            //   }));
            // }
          }}
          onSave={(data, payload) =>
            handleSectionSave("shipmentConfig", data, payload)
          }
          onDiscard={() => handleSectionDiscard("shipmentConfig")}
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("shipmentConfig", isEditing)
          }
          disableEdit={true} // We're handling edit state in the parent now
        />

        {/* PO Configurations Section */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">PO Configurations</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "poConfigurations" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("poConfigurations")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (poConfigurationsRef.current) {
                      if (poConfigurationsRef.current.validateForm()) {
                        const data = poConfigurationsRef.current.getFormData();
                        const payload =
                          poConfigurationsRef.current.getPayload();
                        handleSectionSave("poConfigurations", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["poConfigurations"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("poConfigurations", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "poConfigurations"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedPOConfigurationsSection
          ref={poConfigurationsRef}
          key={`po-configurations-${currentlyEditingSection === "poConfigurations"
            }`}
          form={sectionFormData.poConfigurations}
          errors={{}}
          editMode={currentlyEditingSection === "poConfigurations"}
          onChange={(e) => {
            if (e.target.name === "formChanged") {
              handleSectionChange("poConfigurations", e.target.value);
            } else {
              const newForm = {
                ...sectionFormData.poConfigurations,
                [e.target.name]: e.target.value,
              };
              setSectionFormData((prev) => ({
                ...prev,
                poConfigurations: newForm,
              }));
              setChangedSections((prev) => ({
                ...prev,
                poConfigurations: true,
              }));
            }
          }}
          onSave={(data, payload) =>
            handleSectionSave("poConfigurations", data, payload)
          }
          onDiscard={() => handleSectionDiscard("poConfigurations")}
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("poConfigurations", isEditing)
          }
          disableEdit={true}
        />

        <div className={classes.sectionHeader}>
          <Typography variant="h6">Product Types</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "productTypes" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("productTypes")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (productTypesRef.current) {
                      // Validate form first
                      if (productTypesRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = productTypesRef.current.getFormData();
                        const payload = productTypesRef.current.getPayload();
                        handleSectionSave("productTypes", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["productTypes"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("productTypes", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "productTypes"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedProductTypesSection
          ref={productTypesRef}
          key={`product-types-${currentlyEditingSection === "productTypes"}`}
          productTypesData={sectionFormData.productTypes}
          isProductsLoading={false}
          editMode={currentlyEditingSection === "productTypes"}
          onChange={() => {
            setChangedSections((prev) => ({
              ...prev,
              productTypes: true,
            }));
          }}
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
        />

        {/* Document Types Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Document Types</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "documentTypes" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("documentTypes")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (documentTypesRef.current) {
                      // Validate form first
                      if (documentTypesRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = documentTypesRef.current.getFormData();
                        const payload = documentTypesRef.current.getPayload();
                        handleSectionSave("documentTypes", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["documentTypes"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("documentTypes", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "documentTypes"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedDocumentTypesSection
          ref={documentTypesRef}
          key={`document-types-${currentlyEditingSection === "documentTypes"}`}
          documentTypesData={sectionFormData.documentTypes}
          isDocLoading={false}
          editMode={currentlyEditingSection === "documentTypes"}
          onSave={(data, payload) =>
            handleSectionSave("documentTypes", data, payload)
          }
          onToggle={() => handleSectionChange("documentTypes", true)}
          onChange={() => {
            setChangedSections((prev) => ({
              ...prev,
              documentTypes: true,
            }));
          }}
          onDiscard={() => handleSectionDiscard("documentTypes")}
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("documentTypes", isEditing)
          }
          disableEdit={true} // We're handling edit state in the parent now
        />

        {/* Document Fetch Details Section */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Document Fetch Details</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "documentFetchDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("documentFetchDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (documentFetchDetailsRef.current) {
                      if (documentFetchDetailsRef.current.validateForm()) {
                        const data =
                          documentFetchDetailsRef.current.getFormData();
                        const payload =
                          documentFetchDetailsRef.current.getPayload();
                        handleSectionSave(
                          "documentFetchDetails",
                          data,
                          payload
                        );
                      }
                    }
                  }}
                  disabled={!changedSections["documentFetchDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("documentFetchDetails", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "documentFetchDetails"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedDocumentFetchDetailsSection
          ref={documentFetchDetailsRef}
          key={`document-fetch-details-${currentlyEditingSection === "documentFetchDetails"
            }`}
          initialData={sectionFormData.documentFetchDetails}
          editMode={currentlyEditingSection === "documentFetchDetails"}
          onChange={(data, hasChanged) =>
            handleSectionChange("documentFetchDetails", hasChanged, data)
          }
          onSave={(data, payload) =>
            handleSectionSave("documentFetchDetails", data, payload)
          }
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("documentFetchDetails", isEditing)
          }
          onDiscard={() => handleSectionDiscard("documentFetchDetails")}
          disableEdit={true}
        />

        {/* Ingestion Configuration Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.formTitle}>Ingestion Configuration</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "ingestionConfig" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("ingestionConfig")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (ingestionConfigRef.current) {
                      if (ingestionConfigRef.current.validateForm()) {
                        const data = ingestionConfigRef.current.getFormData();
                        const payload =
                          ingestionConfigRef.current.getPayload();
                        handleSectionSave("ingestionConfig", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["ingestionConfig"] || !isIngestionConfigValid}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("ingestionConfig", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "ingestionConfig"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedIngestionConfigurationSection
          ref={ingestionConfigRef}
          key={`ingestion-config-${currentlyEditingSection === "ingestionConfig"}`}
          initialData={sectionFormData.ingestionConfig}
          editMode={currentlyEditingSection === "ingestionConfig"}
          onChange={(data, hasChanged) =>
            handleSectionChange("ingestionConfig", hasChanged, data)
          }
          onSave={(data, payload) =>
            handleSectionSave("ingestionConfig", data, payload)
          }
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("ingestionConfig", isEditing)
          }
          onDiscard={() => handleSectionDiscard("ingestionConfig")}
          disableEdit={true}
          dataElements={dataElements}
          isDataElementsLoading={isDataElementsLoading}
        />

        {/* Purchasing Details Section with centralized control buttons */}
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Purchasing Details</Typography>
          <Box
            className="section-actions"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {currentlyEditingSection === "purchasingDetails" ? (
              <Box className={classes.actionButtons}>
                <Button
                  variant="outlined"
                  className={classes.discardButton}
                  onClick={() => handleSectionDiscard("purchasingDetails")}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  className={classes.saveButton}
                  onClick={() => {
                    if (purchasingDetailsRef.current) {
                      // Validate form first
                      if (purchasingDetailsRef.current.validateForm()) {
                        // Get the form data and payload
                        const data = purchasingDetailsRef.current.getFormData();
                        const payload =
                          purchasingDetailsRef.current.getPayload();
                        handleSectionSave("purchasingDetails", data, payload);
                      }
                    }
                  }}
                  disabled={!changedSections["purchasingDetails"]}
                >
                  Save Changes
                </Button>
              </Box>
            ) : (
              <IconButton
                onClick={() =>
                  handleSectionEditModeChange("purchasingDetails", true)
                }
                className={classes.iconButtonStyle}
                disabled={
                  currentlyEditingSection !== null &&
                  currentlyEditingSection !== "purchasingDetails"
                }
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </div>
        <MemoizedPurchasingDetailsSection
          ref={purchasingDetailsRef}
          key={`purchasing-details-${currentlyEditingSection === "purchasingDetails"
            }`}
          initialData={sectionFormData.purchasingDetails}
          onChange={(hasChanged) =>
            handleSectionChange("purchasingDetails", hasChanged)
          }
          editMode={currentlyEditingSection === "purchasingDetails"}
          onEditModeChange={(isEditing) =>
            handleSectionEditModeChange("purchasingDetails", isEditing)
          }
          vendorMosymphonyID={cmoVendorDetails?.vendorMosymphonyID}
          brandOwnerMosymphonyID={cmoVendorDetails?.brandOwnerMosymphonyID}
          disableEdit={true} // We're handling edit state in the parent now
        />

        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
        >
          <Button
            // className={
            //   hasUnsavedChanges || !hasAnyChanges
            //     ? classes.disabledButton
            //     : classes.updateButton
            // }
            onClick={handleUpdate}
            disabled={hasUnsavedChanges || !hasAnyChanges || isUpdating}
            className={classes.updateChangesButton}
            variant="contained"
          >
            {isUpdating ? (
              <React.Fragment>
                Update Changes{" "}
                <CircularProgress
                  size={24}
                  color="inherit"
                  style={{ marginLeft: 10 }}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>Update Changes</React.Fragment>
            )}
          </Button>
        </div>
      </div>

      {/* Debug verification button - only for development purposes */}
      {/* <Button
        variant="outlined"
        style={{ marginLeft: 16, marginTop: 20 }}
        onClick={verifyFinalPayload}
      >
        Verify Payload
      </Button> */}

      {/* <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar> */}

      {/* Success Dialog */}
      <Dialog
        open={showSuccessPopup}
        onClose={handlePopupOk}
        fullWidth
        className="cmo-updated-success-popup"
        maxWidth="sm"
      >
        <StyledDialog
          title="CMO Details Updated"
          subtitle="The CMO details have been updated successfully."
          icon={false} // false means show success (check), true means error (cross)
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>

      {/* Loading overlay when redirecting */}
      {isRedirecting && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={3000}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      )}

      {/* Loading overlay when updating CMO configuration */}
      {isUpdating && !isRedirecting && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={3000}
        >
          <CircularProgress size={60} color="primary" />
          {/* <Typography variant="h6" style={{ marginTop: 20 }}>
            Updating CMO Configuration...
          </Typography> */}
        </Box>
      )}
    </Paper>
  );
}
